// src/lib/opsdesk/api.ts
//
// Live ops-desk API client (repairs / quotations / equipment lending).
// All endpoints are staff-gated; every call sends authHeaders() and must
// only run after auth readiness (callers gate on
// auth.isReady && auth.isAuthenticated). No localStorage anywhere — the
// Shop database is authoritative.

import { authHeaders } from "@/lib/api";

export type RepairStatus = "triage" | "in-repair" | "vendor-routing" | "closed";
export type QuoteStatus = "draft" | "sent" | "accepted" | "refused" | "expired";
export type LoanStatus = "out" | "overdue" | "returned";

export interface RepairTicketRow {
  ticket_id: string;
  customer_id: number;
  customer_name: string;
  product_id: number | null;
  subject: string;
  status: RepairStatus;
  note: string | null;
  created_by: string;
  created_at: string | null;
  updated_at: string | null;
}

export interface QuotationRow {
  quote_id: string;
  customer_id: number;
  customer_name: string;
  subject: string;
  amount_htva: string | null;
  status: QuoteStatus;
  note: string | null;
  created_by: string;
  created_at: string | null;
  updated_at: string | null;
  /** A removed quotation keeps its history; it is out of the active list. */
  removed_at?: string | null;
  removed_by?: string | null;
}

export interface EquipmentLoanRow {
  loan_id: string;
  customer_id: number;
  customer_name: string;
  product_id: number;
  quantity: number;
  status: LoanStatus;
  out_at: string | null;
  due_at: string | null;
  returned_at: string | null;
  note: string | null;
  created_by: string;
}

export interface CustomerSearchRow {
  customer_id: number;
  name: string;
  city: string | null;
  vat_number: string | null;
}

/** Next repair status, mirroring the server-side advance-only chain. */
export const REPAIR_NEXT_STATUS: Record<RepairStatus, RepairStatus | null> = {
  triage: "in-repair",
  "in-repair": "vendor-routing",
  "vendor-routing": "closed",
  closed: null,
};

/** Legal quotation targets per status, mirroring the server-side map. */
export const QUOTE_TRANSITIONS: Record<QuoteStatus, QuoteStatus[]> = {
  draft: ["sent", "expired"],
  sent: ["accepted", "refused", "expired"],
  accepted: [],
  refused: [],
  expired: [],
};

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const r = await fetch(path, {
    ...init,
    headers: { ...authHeaders(), ...(init?.headers ?? {}) },
  });
  if (!r.ok) {
    let detail = `HTTP ${r.status}`;
    try {
      const body = (await r.json()) as { detail?: { code?: string } | string };
      if (typeof body.detail === "object" && body.detail?.code) {
        detail = `${detail} ${body.detail.code}`;
      } else if (typeof body.detail === "string" && body.detail) {
        // The server's plain message is the actionable part. Without
        // it the operator sees only "HTTP 403" and not the reason.
        detail = `${detail} ${body.detail}`;
      }
    } catch {
      /* non-JSON error body — keep the status */
    }
    throw new Error(detail);
  }
  return (await r.json()) as T;
}

function post<T>(path: string, body?: unknown): Promise<T> {
  return request<T>(path, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body ?? {}),
  });
}

// ---------------------------------------------------------------- repairs

export function fetchRepairs(status?: RepairStatus | "all") {
  const q = status && status !== "all" ? `?status=${status}` : "";
  return request<{ rows: RepairTicketRow[] }>(`/api/repairs${q}`);
}

export function createRepair(body: {
  customer_id: number;
  product_id?: number | null;
  subject: string;
  note?: string | null;
}) {
  return post<RepairTicketRow>("/api/repairs", body);
}

export function advanceRepair(ticketId: string, note?: string) {
  return post<RepairTicketRow>(
    `/api/repairs/${encodeURIComponent(ticketId)}/advance`,
    note ? { note } : {},
  );
}

// ------------------------------------------------------------- quotations

export function fetchQuotations(status?: QuoteStatus | "all", includeRemoved = false) {
  const q = status && status !== "all" ? `?status=${status}` : "";
  const removed = includeRemoved ? `${q ? "&" : "?"}include_removed=true` : "";
  return request<{ rows: QuotationRow[] }>(`/api/quotations${q}${removed}`);
}

export function createQuotation(body: {
  customer_id: number;
  subject: string;
  amount_htva?: string | null;
  note?: string | null;
}) {
  return post<QuotationRow>("/api/quotations", body);
}

export function setQuotationStatus(quoteId: string, toStatus: QuoteStatus) {
  return post<QuotationRow>(
    `/api/quotations/${encodeURIComponent(quoteId)}/status`,
    { to_status: toStatus },
  );
}

/**
 * Remove a quotation from the active list. ADMIN ONLY — the server
 * refuses anyone else.
 *
 * The record is NOT destroyed: the server marks it removed, so its
 * history survives and an export record that already refers to it stays
 * valid. The removal is a web-side action only and is never propagated
 * to FileMaker.
 */
export function removeQuotation(quoteId: string) {
  return request<QuotationRow>(
    `/api/quotations/${encodeURIComponent(quoteId)}`,
    { method: "DELETE" },
  );
}

/**
 * Correct a DRAFT quotation. ADMIN ONLY — the server refuses anyone
 * else, and the server only edits a draft: once a quotation is sent
 * the customer may have seen it, and the terminal states are frozen.
 * Only the fields passed are changed; an explicit null clears an
 * optional field (amount, note).
 */
export function updateQuotation(
  quoteId: string,
  body: {
    customer_id?: number;
    subject?: string;
    amount_htva?: string | null;
    note?: string | null;
  },
) {
  return request<QuotationRow>(
    `/api/quotations/${encodeURIComponent(quoteId)}`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
  );
}

// ---------------------------------------------------------------- lending

export function fetchLoans(status?: LoanStatus | "all") {
  const q = status && status !== "all" ? `?status=${status}` : "";
  return request<{ rows: EquipmentLoanRow[] }>(`/api/lending${q}`);
}

export function createLoan(body: {
  customer_id: number;
  product_id: number;
  quantity: number;
  due_at?: string | null;
  note?: string | null;
}) {
  return post<EquipmentLoanRow>("/api/lending", body);
}

export function returnLoan(loanId: string) {
  return post<EquipmentLoanRow>(
    `/api/lending/${encodeURIComponent(loanId)}/return`,
  );
}

// -------------------------------------------------------------- customers

export function searchCustomers(q: string, limit = 8) {
  const p = new URLSearchParams({ q, limit: String(limit) });
  return request<{ rows: CustomerSearchRow[] }>(`/api/customers?${p}`);
}
